import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { fileUrl, formatPrice, formatDate } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Label } from "../../components/ui/label";
import { ArrowLeft, ImageOff } from "lucide-react";

const ORDER_STATES = ["Nuovo", "Pagato", "In preparazione", "Spedito", "Consegnato", "Annullato"];
const PAY_STATES = ["In attesa", "Pagato", "Pagamento fallito", "Rimborsato"];

export default function AdminOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [o, setO] = useState(null);

  const load = useCallback(() => api.get(`/admin/orders/${id}`).then(({ data }) => setO(data)).catch(() => setO(false)), [id]);
  useEffect(() => { load(); }, [load]);

  if (o === false) return <p className="text-slate-400">Ordine non trovato.</p>;
  if (!o) return <p className="text-slate-400">Caricamento...</p>;

  const update = async (field, value) => {
    await api.put(`/admin/orders/${id}/status`, { [field]: value });
    load(); toast.success("Aggiornato");
  };

  return (
    <div className="max-w-4xl">
      <button onClick={() => navigate("/admin/ordini")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-primary mb-4"><ArrowLeft className="w-4 h-4" /> Ordini</button>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl sm:text-3xl font-extrabold font-display text-slate-900">{o.order_number}</h1>
        <span className="text-sm text-slate-400">{formatDate(o.created_at)}</span>
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <Card className="p-5 rounded-2xl border-slate-200">
          <h3 className="font-display font-bold text-slate-900 mb-3">Dati cliente</h3>
          <p className="text-sm text-slate-600">{o.customer.nome} {o.customer.cognome}<br />{o.customer.email}<br />{o.customer.telefono}</p>
        </Card>
        <Card className="p-5 rounded-2xl border-slate-200">
          <h3 className="font-display font-bold text-slate-900 mb-3">Spedizione</h3>
          <p className="text-sm text-slate-600">{o.shipping.indirizzo} {o.shipping.civico}<br />{o.shipping.cap} {o.shipping.comune} ({o.shipping.provincia})<br />{o.shipping.nazione}{o.shipping.note && <><br /><span className="italic text-slate-400">Note: {o.shipping.note}</span></>}</p>
        </Card>
      </div>

      <Card className="mt-6 p-5 rounded-2xl border-slate-200">
        <h3 className="font-display font-bold text-slate-900 mb-3">Prodotti</h3>
        <div className="space-y-3">
          {o.items.map((it, i) => (
            <div key={i} className="flex items-center gap-3 text-sm">
              <div className="w-12 h-12 rounded-lg bg-emerald-50 overflow-hidden flex items-center justify-center shrink-0">{fileUrl(it.image_url) ? <img src={fileUrl(it.image_url)} alt="" className="w-full h-full object-cover" /> : <ImageOff className="w-4 h-4 text-emerald-200" />}</div>
              <span className="flex-1">{it.name}</span>
              <span className="text-slate-500">{formatPrice(it.price)} × {it.quantity}</span>
              <span className="font-medium w-20 text-right">{formatPrice(it.line_total)}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 border-t border-slate-100 pt-3 space-y-1 text-sm">
          <div className="flex justify-between text-slate-500"><span>Subtotale</span><span>{formatPrice(o.subtotal)}</span></div>
          <div className="flex justify-between text-slate-500"><span>Spedizione</span><span>{o.shipping_cost === 0 ? "Gratuita" : formatPrice(o.shipping_cost)}</span></div>
          <div className="flex justify-between font-bold text-lg"><span>Totale</span><span>{formatPrice(o.total)}</span></div>
        </div>
      </Card>

      <Card className="mt-6 p-5 rounded-2xl border-slate-200 grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="mb-1.5 block">Stato ordine</Label>
          <select value={o.order_status} onChange={(e) => update("order_status", e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="detail-order-status">
            {ORDER_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <Label className="mb-1.5 block">Stato pagamento</Label>
          <select value={o.payment_status} onChange={(e) => update("payment_status", e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="detail-pay-status">
            {PAY_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </Card>
    </div>
  );
}
